import {
  ChevronLeft, ChevronRight, Search, X, Download, Upload, BellRing, Repeat, MapPin,
} from "lucide-react";
import { colourOf, REPEAT_LABELS } from "./palette";

const VIEW_KEYS = ["day", "week", "month", "year", "agenda"];

export const CalendarToolbar = ({
  title, views, view, setView, accent, offToday, hideNav,
  onShift, onToday, query, setQuery, hits, onJump,
  fileInput, onImport, onExport, importing,
}) => (
  <>
    <div className="mt-3 flex flex-wrap items-center gap-3">
      <h1 className="font-display text-[1.7rem] font-extrabold tracking-tighter sm:text-[2.25rem]"
          data-testid="calendar-month-title">
        {title}
      </h1>
      <div className={`flex items-center gap-1.5 ${hideNav ? "hidden" : ""}`}>
        <button
          data-testid="calendar-prev-month"
          onClick={() => onShift(-1)}
          aria-label="Go back"
          className="rounded-full border border-white/10 p-2 text-ink-soft transition-colors hover:text-ink"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          data-testid="calendar-next-month"
          onClick={() => onShift(1)}
          aria-label="Go forward"
          className="rounded-full border border-white/10 p-2 text-ink-soft transition-colors hover:text-ink"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        {offToday && (
          <button
            data-testid="calendar-today"
            onClick={onToday}
            className="rounded-full border px-4 py-1.5 text-xs font-semibold"
            style={{ borderColor: accent, color: accent }}
          >
            Today
          </button>
        )}
      </div>
      <span className="relative ms-auto w-full sm:w-60">
        <Search className="pointer-events-none absolute start-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-soft" />
        <input
          data-testid="calendar-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search my calendar"
          placeholder="Search my calendar"
          className="min-h-[40px] w-full rounded-full border border-white/10 bg-panel ps-9 pe-9 text-sm outline-none placeholder:text-ink-soft"
        />
        {query && (
          <button
            data-testid="calendar-search-clear"
            onClick={() => setQuery("")}
            aria-label="Clear search"
            className="absolute end-3 top-1/2 -translate-y-1/2 text-ink-soft hover:text-ink"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    </div>

    <div className="mt-4 flex flex-wrap items-center gap-2">
      <div className="flex flex-wrap rounded-full border border-white/10 p-1" data-testid="calendar-views"
           role="tablist" aria-label="Calendar views">
        {VIEW_KEYS.map((key) => {
          const { label, Icon } = views[key];
          return (
            <button
              key={key}
              data-testid={`calendar-view-${key}`}
              onClick={() => setView(key)}
              role="tab"
              aria-selected={view === key}
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors"
              style={view === key ? { backgroundColor: accent, color: "#0B0F14" } : { color: "#9AA3B2" }}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          );
        })}
      </div>
      <input
        ref={fileInput}
        type="file"
        accept=".ics,text/calendar"
        onChange={onImport}
        className="hidden"
        data-testid="calendar-import-input"
      />
      <button
        data-testid="calendar-import"
        onClick={() => fileInput.current?.click()}
        disabled={importing}
        className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-white/10 px-4 text-xs font-semibold text-ink-soft transition-colors hover:text-ink"
      >
        <Upload className="h-3.5 w-3.5" /> {importing ? "Bringing it in…" : "Import .ics"}
      </button>
      <button
        data-testid="calendar-export"
        onClick={onExport}
        className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-white/10 px-4 text-xs font-semibold text-ink-soft transition-colors hover:text-ink"
      >
        <Download className="h-3.5 w-3.5" /> Export .ics
      </button>
    </div>

    {hits && (
      <div className="mt-4 rounded-2xl border border-white/10 bg-panel p-4" data-testid="calendar-search-results">
        {hits.events.length + hits.reminders.length === 0 ? (
          <p className="text-sm text-ink-soft">Nothing in your calendar matches “{hits.q}”.</p>
        ) : (
          <div className="space-y-2">
            {hits.events.map((ev) => (
              <button
                key={ev.event_id}
                data-testid={`search-hit-${ev.event_id}`}
                onClick={() => onJump(ev.day)}
                className="flex w-full flex-wrap items-center gap-2 rounded-xl border border-white/5 px-3 py-2 text-start text-sm hover:border-white/20"
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colourOf(ev.colour) }} />
                <span className="font-semibold" dir="auto">{ev.title}</span>
                <span className="text-xs text-ink-soft">{ev.day}</span>
                {ev.repeat !== "none" && (
                  <span className="inline-flex items-center gap-1 text-xs text-ink-soft">
                    <Repeat className="h-3 w-3" /> {REPEAT_LABELS[ev.repeat]}
                  </span>
                )}
                {ev.location && (
                  <span className="inline-flex items-center gap-1 text-xs text-ink-soft">
                    <MapPin className="h-3 w-3" /> {ev.location}
                  </span>
                )}
              </button>
            ))}
            {hits.reminders.map((r) => (
              <button
                key={r.reminder_id}
                data-testid={`search-hit-${r.reminder_id}`}
                onClick={() => onJump(r.day)}
                className="flex w-full flex-wrap items-center gap-2 rounded-xl border border-white/5 px-3 py-2 text-start text-sm hover:border-white/20"
              >
                <BellRing className="h-3.5 w-3.5" style={{ color: accent }} />
                <span dir="auto" className={r.done ? "text-ink-soft line-through" : ""}>{r.text}</span>
                <span className="text-xs text-ink-soft">{r.day}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    )}
  </>
);
